import throttle from 'lodash/throttle';
import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';
import {
	useDispatch,
	useSelector,
} from 'react-redux';

import type CK from '~/types/contentking';
import GraphQL from '~/types/graphql';

import BasicIcon, {
	BasicIconType,
} from '~/components/patterns/icons/BasicIcon';
import Button, {
	ButtonSize,
	ButtonStyle,
	ButtonWidth,
} from '~/components/patterns/buttons/Button';
import List, {
	ListSize,
} from '~/components/patterns/lists/List';
import WebsitesList from '~/components/patterns/websites/WebsitesList';
import WebsitesSearchForm from '~/components/atoms/forms/forms/WebsitesSearchForm';
import WebsiteLink from '~/components/app/WebsiteLink';
import Website from '~/components/patterns/websites/Website';
import WebsiteFavicon, {
	WebsiteFaviconUsageContext,
} from '~/components/logic/websites/WebsiteFavicon';
import StatusFlag, {
	StatusFlagStatus,
} from '~/components/patterns/statuses/StatusFlag';
import Hint, {
	HintAttachment,
} from '~/components/patterns/hints/hint/Hint';

import {
	useTeamWebsitesDropdownQuery,
} from './TeamWebsitesDropdown.gql';

import useEnabledColumnsList from '~/hooks/useEnabledColumnsList';
import useIsAllowedWithAccount from '~/hooks/useIsAllowedWithAccount';
import usePollInterval from '~/hooks/usePollInterval';
import useSelectedWebsiteId from '~/hooks/useSelectedWebsiteId';
import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useViewportType from '~/hooks/useViewportType';
import useWebsiteDisplayName from '~/hooks/useWebsiteDisplayName';
import useWebsiteIsCrawlingPaused from '~/hooks/useWebsiteIsCrawlingPaused';

import {
	useSelectedAccount,
} from '~/contexts/SelectedAccountContext';

import {
	isPhase3,
} from '~/utilities/viewportSize';

import {
	menu,
	storeLastSearchedWebsite,
} from '~/actions';

import {
	filterWebsitesByName,
} from '~/model/websites';

import {
	lastSearchedWebsiteSelector,
} from '~/state/lastSearchedWebsite/selectors';

import {
	filterSelector as historicalChangesFilterSelector,
	dateIntervalSelector as historicalChangesIntervalSelector,
	sortBySelector as historicalChangesSortBySelector,
	isInHistoricalChangesModeSelector,
} from '~/state/historicalChanges/selectors';

import {
	pagesColumnsParameterSelector,
	pagesViewParameterSelector,
} from '~/state/ui/content/selectors';

import {
	filterSelector as pagesFilterSelector,
	sortBySelector as pagesSortBySelector,
	selectedSectionSelector,
} from '~/state/ui/selectors';

import sortArrayByProperty from '~/utilities/sortArrayByProperty';



const messages = defineMessages({
	addWebsite: {
		id: 'ui.menu.websites.addWebsiteLabel',
	},
	progressDescription: {
		id: 'ui.menu.progress.tooltip',
	},
});



type Props = {
	selectedAccountId: CK.AccountId,
};

/**
 * Inline Websites Section - New Navigation
 *
 * Simple inline container for websites list, search box, and add button.
 * Renders directly inside Technical section's sub-items using data attributes pattern
 * (same as Secondary Navigation Items).
 *
 * No Dropdown wrapper, no positioning logic - just normal document flow.
 */
const InlineWebsitesSection: React.FC<Props> = (props) => {
	const {
		selectedAccountId,
	} = props;

	const dispatch = useDispatch();
	const { setSelectedAccountId } = useSelectedAccount();

	// Collapse websites dropdown when user selects a website from the list
	const handleWebsiteSelect = React.useCallback(
		() => {
			setSelectedAccountId(null);
			dispatch(menu.closeWebsiteSwitcher());
		},
		[dispatch, setSelectedAccountId],
	);

	const columns = useEnabledColumnsList();
	const columnsParameter = useSelector(pagesColumnsParameterSelector);
	const historicalChangesFilter = useSelector(historicalChangesFilterSelector);
	const historicalChangesInterval = useSelector(historicalChangesIntervalSelector);
	const historicalChangesSortBy = useSelector(historicalChangesSortBySelector);
	const inHistoricalChangesMode = useSelector(isInHistoricalChangesModeSelector);
	const lastSearchedWebsite = useSelector(lastSearchedWebsiteSelector);
	const pagesFilter = useSelector(pagesFilterSelector);
	const pagesSortBy = useSelector(pagesSortBySelector);
	const selectedSection = useSelector(selectedSectionSelector);
	const viewParameter = useSelector(pagesViewParameterSelector);
	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		,
		isMenuMinimized,
	] = useMenuExpandedState();

	const { data } = useTeamWebsitesDropdownQuery({
		pollInterval: usePollInterval(60000),
	});

	const accounts = data?.authenticatedSession?.accounts ?? [];
	const selectedAccount = accounts.find((account) => account.id === selectedAccountId);

	// Add website button - must be called before early return
	const isAllowedToAddWebsite = useIsAllowedWithAccount(
		selectedAccountId,
		GraphQL.ActionWithAccount.AddWebsite,
	);

	// Search box callback - must be called before early return
	const handleSearchCallback = React.useCallback(
		(field, value) => {
			if (field === 'search') {
				dispatch(
					storeLastSearchedWebsite(value),
				);
			}
		},
		[
			dispatch,
		],
	);

	const throttledSearchCallback = React.useMemo(
		() => throttle(handleSearchCallback, 100),
		[handleSearchCallback],
	);

	// Don't render if account not found
	if (!selectedAccount) {
		return null;
	}

	const numberOfWebsites = selectedAccount.websites.length;

	// Get websites for selected account only
	const websites = sortArrayByProperty(selectedAccount.websites, (website) => website.displayName);

	// Filter websites by search term (same logic as TeamWebsitesDropdown)
	const filteredWebsites = (
		(lastSearchedWebsite && lastSearchedWebsite.length > 0 && websites.length > 0)
			? filterWebsitesByName({
				websites,
				name: lastSearchedWebsite,
			})
			: websites
	);

	return (
		<>
			{/* Search box */}
			{numberOfWebsites > 0 && (
				<li
					className="main-nav__item"
					data-websites-section="true"
					data-websites-section-part="search"
					key="websites-search"
				>
					<WebsitesSearchForm
						autoFocus={false}
						defaultValue={lastSearchedWebsite}
						onChangeCallback={throttledSearchCallback}
					/>
				</li>
			)}
			{/* Scrollable container: websites list + add button (own scrollbar, excludes search box and dropdown trigger) */}
			<li
				className="main-nav__item"
				data-websites-section="true"
				data-websites-section-part="scrollable"
				key="websites-list-scrollable"
			>
				<div className="main-nav__websites-list-scrollable">
					<List size={ListSize.Small}>
						<WebsitesList
							hasGaps={false}
							key={selectedAccount.id}
							multiColumns={false}
							placeholder="No websites"
						>
							{filteredWebsites.map((website) => (
								<WebsiteLink
									columns={columns}
									columnsParameter={columnsParameter}
									historicalChangesFilter={historicalChangesFilter}
									historicalChangesInterval={historicalChangesInterval}
									historicalChangesSortBy={historicalChangesSortBy}
									inHistoricalChangesMode={inHistoricalChangesMode}
									isActive={false}
									key={website.id}
									onClick={handleWebsiteSelect}
									pagesFilter={pagesFilter}
									pagesSortBy={pagesSortBy}
									section={selectedSection}
									viewParameter={viewParameter}
									websiteId={website.id}
								>
									<WebsiteLinkContent
										isWebsiteDiscovered={website.isDiscovered}
										websiteId={website.id}
									/>
								</WebsiteLink>
							))}
						</WebsitesList>
					</List>
					{isAllowedToAddWebsite.yes && (
						<Button
							icon={(
								<BasicIcon
									color="#ffffff"
									type={BasicIconType.Plus}
								/>
							)}
							linkRouteName="account.websites.new"
							linkRouteParams={{
								accountId: selectedAccountId,
							}}
							size={ButtonSize.Small}
							style={ButtonStyle.Action}
							width={ButtonWidth.Fullwidth}
						>
							{/* Phase 2: icon only, Phase 3: icon + "Add website" text */}
							{isPhase3(viewportType, isMenuExpanded, isMenuMinimized) && (
								<FormattedMessage {...messages.addWebsite} />
							)}
						</Button>
					)}
				</div>
			</li>
		</>
	);
};



/**
 * Component to render website content (favicon, name, status flag)
 * Extracted from TeamWebsitesDropdown pattern
 */
const WebsiteLinkContent: React.FC<{
	websiteId: CK.WebsiteId,
	isWebsiteDiscovered: boolean | null,
}> = (props) => {
	const {
		websiteId,
		isWebsiteDiscovered,
	} = props;

	const isCrawlingPaused = useWebsiteIsCrawlingPaused(websiteId);
	const selectedWebsiteId = useSelectedWebsiteId();
	const websiteDisplayName = useWebsiteDisplayName(websiteId);

	let iconStatus: StatusFlagStatus | null = null;

	if (isWebsiteDiscovered === false) {
		iconStatus = StatusFlagStatus.DiscoveryInProgress;
	}

	if (isCrawlingPaused) {
		iconStatus = StatusFlagStatus.Paused;
	}

	const renderStatusFlag = () => {
		if (iconStatus === null) {
			return undefined;
		}

		if (iconStatus === StatusFlagStatus.DiscoveryInProgress) {
			return (
				<Hint
					attachment={HintAttachment.Left}
					popup={(
						<FormattedMessage {...messages.progressDescription} />
					)}
					popupMaxWidth={260}
					popupZIndex={1500}
				>
					<StatusFlag status={iconStatus} />
				</Hint>
			);
		}

		return (
			<StatusFlag status={iconStatus} />
		);
	};

	return (
		<Website
			favicon={(
				<WebsiteFavicon
					usageContext={WebsiteFaviconUsageContext.Sidebar}
					websiteId={websiteId}
				/>
			)}
			isHighlighted={websiteId === selectedWebsiteId}
			name={websiteDisplayName}
			statusFlag={renderStatusFlag()}
		/>
	);
};



export default InlineWebsitesSection;
